import { ApiError, type ApiErrorBody } from './api-errors';
import { clearSupportAccessToken, getSupportAccessToken } from './support-access';
import {
  clearTokens,
  getStoredAccessToken,
  getStoredRefreshToken,
  saveTokens,
} from './session-tokens';

/**
 * The only place the console talks to the API.
 *
 * Sessions are httpOnly cookies first; when the API is on another origin, refresh and
 * access tokens are also kept in localStorage and sent as Bearer + body refresh, matching
 * Weive CRM's backup-local pattern. The church context travels in a header rather than
 * the path, because a churchId in a URL invites the mistake of trusting it.
 */

export { clearTokens, getStoredAccessToken, saveTokens };

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:5311/api/v1';
const CLIENT_ID = import.meta.env.VITE_CLIENT_ID ?? 'console';
const CLIENT_VERSION = import.meta.env.VITE_CLIENT_VERSION ?? '0.1.0';

type ChurchIdSource = () => string | null;

let resolveChurchId: ChurchIdSource = () => null;
let onSessionLost: () => void = () => {};

/** Wired by the church store in Phase 2 and the auth store in Phase 1. */
export const configureApi = (options: {
  churchId?: ChurchIdSource;
  onSessionLost?: () => void;
}): void => {
  if (options.churchId) resolveChurchId = options.churchId;
  if (options.onSessionLost) onSessionLost = options.onSessionLost;
};

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Required on anything a retry could turn into a second money record. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Escape hatch for endpoints that are deliberately outside the church context. */
  withoutChurch?: boolean;
  /** Overrides the active church header for one request. */
  churchId?: string;
}

export const api = {
  get: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'POST', body }),
  /** Multipart upload — do not set Content-Type; the browser sets the boundary. */
  postForm: <T>(
    path: string,
    body: FormData,
    options: Omit<RequestOptions, 'method' | 'body'> = {},
  ) => request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options: Omit<RequestOptions, 'method'> = {}) =>
    request<T>(path, { ...options, method: 'DELETE' }),
  /** Binary responses (logos for canvas downloads) — not JSON. */
  getBlob: (path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    requestBlob(path, { ...options, method: 'GET' }),
};

/**
 * A single in-flight refresh, shared by everything that got a 401 at once.
 *
 * Without this, a dashboard loading six panels on an expired session fires six
 * refreshes; five of them present an already-rotated refresh token, which the server
 * correctly treats as a replay and invalidates the session — signing the user out
 * for the crime of opening a page.
 */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Endpoints that must not trigger a silent refresh on 401.
 *
 * `/auth/me` and `/auth/logout` are deliberately *not* skipped: a hard refresh (or
 * Sign out) often hits them with an expired access cookie but a still-valid refresh
 * cookie. Skipping refresh there bounced signed-in people to the sign-in screen, or
 * left cookies behind after a local sign-out.
 */
const shouldSkipRefresh = (path: string): boolean =>
  path === '/auth/refresh' ||
  path === '/auth/login' ||
  path === '/auth/register' ||
  path === '/auth/verify-email' ||
  path === '/auth/resend-verification' ||
  path === '/auth/request-password-reset' ||
  path === '/auth/reset-password' ||
  path.startsWith('/auth/login-requests') ||
  path.startsWith('/auth/qr-login') ||
  path === '/support-access/redeem';

const request = async <T>(path: string, options: RequestOptions): Promise<T> => {
  const response = await send(path, options);

  if (response.status !== 401 || shouldSkipRefresh(path)) {
    return handle<T>(response);
  }

  if (getSupportAccessToken()) {
    clearSupportAccessToken();
    onSessionLost();
    return handle<T>(response);
  }

  const refreshed = await refreshSession();
  if (!refreshed) {
    onSessionLost();
    return handle<T>(response);
  }

  return handle<T>(await send(path, options));
};

const requestBlob = async (path: string, options: RequestOptions): Promise<Blob> => {
  const response = await send(path, options);

  if (response.status === 401 && !shouldSkipRefresh(path)) {
    if (getSupportAccessToken()) {
      clearSupportAccessToken();
      onSessionLost();
      throw new ApiError(response.status);
    }
    const refreshed = await refreshSession();
    if (!refreshed) {
      onSessionLost();
      throw new ApiError(response.status);
    }
    return requestBlob(path, options);
  }

  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw new ApiError(response.status, body as ApiErrorBody | undefined);
  }

  return response.blob();
};

const send = async (path: string, options: RequestOptions): Promise<Response> => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    // Buys three things: an honest answer to "can we retire v1", per-client error
    // rates, and the ability to tell an old app it needs updating.
    'X-Trinity-Client': `${CLIENT_ID}/${CLIENT_VERSION}`,
  };

  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  if (options.body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;

  if (!options.withoutChurch) {
    const churchId = options.churchId ?? resolveChurchId();
    if (churchId) headers['X-Church-Id'] = churchId;
  }

  const supportToken = getSupportAccessToken();
  if (supportToken) headers['X-Support-Access'] = supportToken;

  const token = getStoredAccessToken();
  if (token && !supportToken) headers.Authorization = `Bearer ${token}`;

  return fetch(`${BASE_URL}${path}${queryString(options.query)}`, {
    method: options.method ?? 'GET',
    headers,
    credentials: 'include',
    cache: 'no-store',
    body:
      options.body === undefined
        ? undefined
        : isFormData
          ? (options.body as FormData)
          : JSON.stringify(options.body),
    signal: options.signal,
  });
};

const refreshHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  'X-Trinity-Client': `${CLIENT_ID}/${CLIENT_VERSION}`,
});

const refreshWithStoredToken = async (): Promise<boolean> => {
  const storedRefreshToken = getStoredRefreshToken();
  if (!storedRefreshToken) return false;

  const response = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: {
      ...refreshHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refreshToken: storedRefreshToken }),
  });

  if (!response.ok) return false;

  const data = (await response.json().catch(() => null)) as {
    accessToken?: string;
    refreshToken?: string;
  } | null;

  if (!data?.accessToken || !data.refreshToken) return false;

  saveTokens(data.accessToken, data.refreshToken);
  return true;
};

const refreshWithCookies = async (): Promise<boolean> => {
  const response = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: refreshHeaders(),
  });
  return response.ok;
};

const REFRESH_LOCK = 'trinity-auth-refresh';

const runRefresh = async (): Promise<boolean> => {
  if (await refreshWithStoredToken()) return true;
  return refreshWithCookies();
};

const refreshSession = (): Promise<boolean> => {
  refreshInFlight ??= (async () => {
    // One tab already coalesces through `refreshInFlight`. Two tabs of the same
    // origin do not share that promise; a Web Lock serialises them so the second
    // spends the token the first just wrote, not the one it already rotated.
    const locks = globalThis.navigator?.locks;
    if (typeof locks?.request === 'function') {
      return locks.request(REFRESH_LOCK, runRefresh);
    }
    return runRefresh();
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};

/**
 * Restores a persisted session on app load using stored refresh tokens or httpOnly cookies.
 *
 * Matching Weive CRM: refresh proactively before `/auth/me`, and swallow a missing
 * session — the caller still probes `/auth/me` to learn whether anyone is signed in.
 */
export const bootstrapSession = async (): Promise<void> => {
  try {
    await refreshSession();
  } catch {
    // No valid persisted session; caller may still try /auth/me.
  }
};

/** Renews the access cookie before it expires (server default: 15 minutes). */
export const keepSessionAlive = async (): Promise<void> => {
  await refreshSession();
};

export type ConsoleSessionPolicy = {
  rememberMeEnabled: boolean;
  idleTimeoutMinutes: number;
};

export const fetchConsoleSessionPolicy = (options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
  api.get<ConsoleSessionPolicy>('/auth/console-session', { withoutChurch: true, ...options });

export const recordSessionActivity = async (): Promise<void> => {
  await api.post('/auth/session/activity', undefined, { withoutChurch: true });
};

const handle = async <T>(response: Response): Promise<T> => {
  if (response.status === 204) return undefined as T;

  const body = await response.json().catch(() => undefined);

  if (!response.ok) {
    throw new ApiError(response.status, body as ApiErrorBody | undefined);
  }
  return body as T;
};

const queryString = (query: RequestOptions['query']): string => {
  if (!query) return '';

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }

  const serialised = params.toString();
  return serialised ? `?${serialised}` : '';
};

/** The shape every collection endpoint returns. */
export interface OffsetPage {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface CursorPage {
  nextCursor: string | null;
  hasMore: boolean;
}

export interface Collection<T, Totals = never> {
  data: T[];
  page: OffsetPage;
  totals?: Totals;
}

export interface CursorCollection<T> {
  data: T[];
  page: CursorPage;
}
